import { useCallback, useMemo, useState, useEffect } from "react";
import {
  crearCiclo,
  actualizarCiclo,
  eliminarCiclo,
  obtenerCiclo,
} from "../../../../services/ciclosService";
import { listarPlantillas } from "../../../../services/convocatoriaPlantillasService";
import { listarCursosPorCentro } from "../../../../services/cursosService";
import { showToast } from "../../../../utils/showToast";
import {
  buildPayload,
  extractCiclo,
  initialFormState,
  normalizeCiclo,
  validarCiclo,
} from "../adapters/ciclosAdapter";
import useCiclosList from "./useCiclosList";
import useCentrosListado from "../../estudiantes/hooks/useCentrosListado";

export default function useCiclosLogic() {
  const [modal, setModal] = useState("");
  const [cicloSeleccionado, setCicloSeleccionado] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);

  const list = useCiclosList();
  const { centros, loadingCentros, centrosError, reloadCentros } = useCentrosListado();

  const stats = useMemo(
    () => [
      {
        key: "all",
        label: "Total ciclos",
        value: list.resumen?.total ?? 0,
        icon: "bi bi-layers",
        accent: "",
      },
      {
        key: "planificado",
        label: "Planificados",
        value: list.resumen?.planificados ?? 0,
        icon: "bi bi-calendar-event",
        accent: "info",
        filter: { tipo: "estado", valor: "planificado" },
      },
      {
        key: "activo",
        label: "Activos",
        value: list.resumen?.activosEstado ?? 0,
        icon: "bi bi-lightning",
        accent: "success",
        filter: { tipo: "estado", valor: "activo" },
      },
      {
        key: "cerrado",
        label: "Cerrados",
        value: list.resumen?.cerrados ?? 0,
        icon: "bi bi-lock",
        accent: "warning",
        filter: { tipo: "estado", valor: "cerrado" },
      },
      {
        key: "cancelado",
        label: "Cancelados",
        value: list.resumen?.cancelados ?? 0,
        icon: "bi bi-x-circle",
        accent: "danger",
        filter: { tipo: "estado", valor: "cancelado" },
      },
    ],
    [list.resumen]
  );

  const defaultCentroId = useMemo(() => {
    if (!centros.length) return "";
    return String(centros[0].id);
  }, [centros]);


  useEffect(() => {
    if (!formNuevo.centro_id && defaultCentroId) {
      setFormNuevo((prev) => ({ ...prev, centro_id: defaultCentroId }));
    }
  }, [defaultCentroId, formNuevo.centro_id]);


  // Cargar plantillas y cursos según el centro activo (formulario nuevo o editar)
  const centroIdActivo = formNuevo.centro_id || formEditar.centro_id;

  const cargarCursos = useCallback((centroId) => {
    if (!centroId) { setCursos([]); return; }
    setLoadingCursos(true);
    listarCursosPorCentro(centroId)
      .then((data) => setCursos(Array.isArray(data) ? data : data?.data ?? []))
      .catch(() => setCursos([]))
      .finally(() => setLoadingCursos(false));
  }, []);

  const reloadCursos = useCallback(() => cargarCursos(centroIdActivo), [cargarCursos, centroIdActivo]);

  useEffect(() => {
    if (!centroIdActivo) {
      setPlantillas([]);
      setCursos([]);
      return;
    }
    let cancelled = false;
    setLoadingPlantillas(true);
    setLoadingCursos(true);
    Promise.all([
      listarPlantillas(centroIdActivo),
      listarCursosPorCentro(centroIdActivo),
    ]).then(([plantillasData, cursosData]) => {
      if (cancelled) return;
      setPlantillas(Array.isArray(plantillasData) ? plantillasData : plantillasData?.data ?? []);
      setCursos(Array.isArray(cursosData) ? cursosData : cursosData?.data ?? []);
    }).catch(() => {
      if (!cancelled) { setPlantillas([]); setCursos([]); }
    }).finally(() => {
      if (!cancelled) { setLoadingPlantillas(false); setLoadingCursos(false); }
    });
    return () => { cancelled = true; };
  }, [centroIdActivo]);

  const abrirModalNuevo = useCallback(() => {
    setFormNuevo({
      ...initialFormState,
      centro_id: defaultCentroId || "",
    });
    setCicloSeleccionado(null);
    setModal("nuevo");
  }, [defaultCentroId]);

  const abrirModalEditar = useCallback(async (ciclo) => {
    if (!ciclo?.id) return;
    setModal("editar");
    setDetalleLoading(true);
    try {
      const data = await obtenerCiclo(ciclo.id);
      const normalized = normalizeCiclo({ ...ciclo, ...data });
      setCicloSeleccionado(normalized);
      setFormEditar({
        centro_id: normalized.centroId ? String(normalized.centroId) : "",
        plantilla_id: normalized.plantillaId ? String(normalized.plantillaId) : "",
        anio: normalized.anio ? String(normalized.anio) : "",
        codigo: normalized.codigo,
        nombre: normalized.nombre,
        fecha_inicio: normalized.fechaInicio || "",
        fecha_fin: normalized.fechaFin || "",
        estado: normalized.estado || "planificado",
        observaciones: normalized.observaciones || "",
        activo: normalized.activo,
        curso_ids: normalized.cursoIds || [],
      });
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo obtener el ciclo.",
      });
      setModal("");
    } finally {
      setDetalleLoading(false);
    }
  }, []);

  const abrirModalEliminar = useCallback((ciclo) => {
    if (!ciclo?.id) return;
    setCicloSeleccionado(normalizeCiclo(ciclo));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setCicloSeleccionado(null);
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
    setDetalleLoading(false);
  }, []);

  const handleCrearCiclo = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarCiclo(formNuevo);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo);
        const creado = await crearCiclo(payload);
        const fallback = {
          id: creado?.ciclo?.id ?? creado?.id,
          centro_id: payload.centro_id,
          anio: payload.anio,
          codigo: payload.codigo,
          nombre: payload.nombre,
          fecha_inicio: payload.fecha_inicio,
          fecha_fin: payload.fecha_fin,
          estado: payload.estado,
          observaciones: payload.observaciones,
          activo: payload.activo,
          created_at:
            creado?.ciclo?.created_at || creado?.created_at || new Date().toISOString(),
        };
        list.upsertCiclo(extractCiclo(creado, fallback));
        showToast({ type: "success", message: "Ciclo creado correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo crear el ciclo.",
        });
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, cerrarModal, list]
  );

  const handleActualizarCiclo = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!cicloSeleccionado) return;

      const validation = validarCiclo(formEditar);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingEditar(true);
      try {
        const payload = buildPayload(formEditar);
        const actualizado = await actualizarCiclo(cicloSeleccionado.id, payload);
        const fallback = {
          ...cicloSeleccionado,
          centro_id: payload.centro_id,
          anio: payload.anio,
          codigo: payload.codigo,
          nombre: payload.nombre,
          fecha_inicio: payload.fecha_inicio,
          fecha_fin: payload.fecha_fin,
          estado: payload.estado,
          observaciones: payload.observaciones,
          activo: payload.activo,
          updated_at: new Date().toISOString(),
        };
        list.upsertCiclo(extractCiclo(actualizado, fallback));
        showToast({ type: "success", message: "Ciclo actualizado correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo actualizar el ciclo.",
        });
      } finally {
        setSavingEditar(false);
      }
    },
    [cicloSeleccionado, formEditar, cerrarModal, list]
  );

  const handleEliminarCiclo = useCallback(async () => {
    if (!cicloSeleccionado) return;
    setDeleting(true);
    try {
      await eliminarCiclo(cicloSeleccionado.id);
      list.removeCiclo(cicloSeleccionado.id);
      showToast({ type: "success", message: "Ciclo eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo eliminar el ciclo.",
      });
    } finally {
      setDeleting(false);
    }
  }, [cicloSeleccionado, cerrarModal, list]);

  const aniosDisponibles = useMemo(() => {
    const set = new Set(list.ciclos.map((ciclo) => String(ciclo.anio || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [list.ciclos]);

  return {
    ciclos: list.ciclosFiltrados,
    ciclosPaginados: list.ciclosPaginados,
    centros,
    loadingCentros,
    centrosError,
    plantillas,
    loadingPlantillas,
    cursos,
    loadingCursos,
    reloadCursos,
    loading: list.loading,
    listError: list.listError,
    resumen: list.resumen,
    stats,
    lastUpdated: list.lastUpdated,
    modal,
    filtro: list.filtro,
    filtros: list.filtros,
    setFiltros: list.setFiltros,
    limpiarFiltros: list.limpiarFiltros,
    page: list.page,
    pageSize: list.pageSize,
    setPageSize: list.setPageSize,
    totalPages: list.totalPages,
    total: list.total,
    goToPage: list.goToPage,
    cicloSeleccionado,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    detalleLoading,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    reload: list.reload,
    reloadCentros,
    aniosDisponibles,
    aplicarFiltro: list.aplicarFiltro,
    handleCrearCiclo,
    handleActualizarCiclo,
    handleEliminarCiclo,
  };
}
